import { FormEvent, PropsWithChildren, useState } from 'react';

import { Button } from './Button';
import { readConfiguredPassword, REVIEWER_SESSION_KEY } from './reviewerSession';

/**
 * Soft reviewer-password gate.
 *
 * When ``VITE_REVIEWER_PASSWORD`` is set at build time, the site is hidden
 * behind a single-input prompt; the reviewer enters the shared password
 * and sessionStorage remembers the pass until the tab is closed. Think
 * "don't index me on Google" rather than security — the password lives
 * in the JS bundle anyway, so the gate is theatre against casual
 * drive-bys, not hostile actors.
 *
 * When the env var is unset (local ``npm run dev`` without a .env.local
 * override, for example), the gate is bypassed entirely so developers
 * aren't interrupted.
 *
 * Design choices
 * --------------
 * - Session lives in ``sessionStorage``, not ``localStorage``. Closing
 *   the tab logs the reviewer out — safer on shared machines.
 * - Constant-time comparison on submit. The bundled secret makes this
 *   largely moot, but it's the right default in case we ever move the
 *   check server-side.
 * - No rate limiting, no captcha, no password recovery flow. All three
 *   are out of scope for the prototype; v0.2 replaces this whole thing
 *   with a real auth provider.
 */

function isAuthedAtBoot(): boolean {
  if (!readConfiguredPassword()) return true; // no password → no gate
  try {
    return sessionStorage.getItem(REVIEWER_SESSION_KEY) === '1';
  } catch {
    // sessionStorage may be unavailable in some embedded contexts.
    return false;
  }
}

/** Constant-time string compare to avoid leaking the password length. */
function constantTimeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) {
    // Run the loop anyway so timing doesn't differ based on length.
    let acc = 0;
    const longer = a.length > b.length ? a : b;
    for (let i = 0; i < longer.length; i++) acc |= 1;
    return acc === 0 && false;
  }
  let diff = 0;
  for (let i = 0; i < a.length; i++) {
    diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return diff === 0;
}

export function ReviewerGate({ children }: PropsWithChildren) {
  const [authed, setAuthed] = useState(isAuthedAtBoot);
  const [pendingValue, setPendingValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (authed) return <>{children}</>;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (constantTimeEqual(pendingValue, readConfiguredPassword())) {
      try {
        sessionStorage.setItem(REVIEWER_SESSION_KEY, '1');
      } catch {
        // If storage is unavailable we still let them through for this
        // session — we just can't remember across reloads.
      }
      setError(null);
      setAuthed(true);
    } else {
      setError('Incorrect password.');
      setPendingValue('');
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-paper text-ink px-4"
      role="main"
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-md border border-border bg-surface-alt/20 p-6 space-y-5"
        aria-labelledby="gate-title"
      >
        <div className="flex items-center gap-3">
          <img
            src="/logo.png"
            alt=""
            className="h-8 w-auto"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).src = '/logo.svg';
            }}
          />
          <h1 id="gate-title" className="text-base font-semibold">
            Raydn reviewer access
          </h1>
        </div>

        <p className="text-xs text-muted">
          This prototype is behind a shared password. Ask the Raydn team
          if you need it. Your access is remembered for this browser tab
          only.
        </p>

        <div>
          <label
            htmlFor="reviewer-password"
            className="block text-xs font-medium uppercase tracking-wide text-muted mb-1"
          >
            Password
          </label>
          <input
            id="reviewer-password"
            type="password"
            autoComplete="current-password"
            autoFocus
            value={pendingValue}
            onChange={(e) => setPendingValue(e.target.value)}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? 'reviewer-password-error' : undefined}
            className="w-full rounded-sm border border-border bg-paper px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40"
          />
          {error && (
            <p
              id="reviewer-password-error"
              role="alert"
              className="mt-2 text-xs text-danger"
            >
              {error}
            </p>
          )}
        </div>

        <Button type="submit" variant="primary" className="w-full">
          Enter
        </Button>
      </form>
    </div>
  );
}

